import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  auth,
  signInWithPopup,
  googleProvider,
  signOut as fbSignOut,
  onAuthStateChanged,
  firebaseConfigured,
} from '../db/firebase_auth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from '../db/firebase_auth';
import { COL } from '../collections';
import { where } from '../collections';
import { useLiveDoc, useLiveQuery } from '../data/hooks';
import type { DosenRoleDoc, MahasiswaDoc, ProdiDoc, SkripsiTeamDoc, UserDoc } from '../types';
import type { CurrentUser } from '../access';

const STORAGE_KEY = 'bimbingan_current_uid';

export type AuthStatus = 'loading' | 'signedOut' | 'unverified' | 'onboarding' | 'signedIn';

interface AuthContextValue {
  status: AuthStatus;
  uid: string | null;
  user: UserDoc | null;
  ctx: CurrentUser | null;
  /** Firebase Auth custom claims from the user's ID token (e.g. `admin`). */
  claims: Record<string, unknown>;
  /** Whether the admin role is recognized: role === "admin" AND custom claim `admin`. */
  isAdmin: boolean;
  /** True once the claims fetch has settled (false while the token loads). */
  claimsReady: boolean;
  /** Email from the Firebase identity provider, if any. */
  email: string | null;
  /** Display name from the Firebase identity provider, if any. */
  displayName: string | null;
  /** Whether the account's email has been verified (email/password only). */
  emailVerified: boolean;
  /** The real signed-in user (admin) — independent of impersonation. */
  realUser: UserDoc | null;
  /** True while the admin is viewing another user's interface. */
  isImpersonating: boolean;
  /** The user currently being impersonated, if any. */
  impersonatedUser: UserDoc | null;
  startImpersonation: (userId: string) => void;
  stopImpersonation: () => void;
  signInGoogle: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  registerEmail: (email: string, password: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  reloadUser: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUid(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [uid, setUid] = useState<string | null>(() => readStoredUid());
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [claims, setClaims] = useState<Record<string, unknown>>({});
  // Tracks whether the claims fetch has settled, so admin-gating doesn't
  // flash a "not recognized" state while the token is still loading.
  const [claimsReady, setClaimsReady] = useState(!firebaseConfigured);
  // Admin-only impersonation: while set, the app renders as that user.
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);

  // Fetch custom claims from the ID token. Force-refreshing the token picks
  // up claims set server-side (e.g. via promote.py) without a full re-login.
  const loadClaims = useCallback(async () => {
    if (!firebaseConfigured) {
      setClaimsReady(true);
      return;
    }
    setClaimsReady(false);
    try {
      const fbUser = auth.currentUser;
      if (!fbUser) {
        setClaims({});
        setClaimsReady(true);
        return;
      }
      const idTokenResult = await fbUser.getIdTokenResult(true);
      setClaims(idTokenResult.claims ?? {});
    } catch {
      setClaims((prev) => prev);
    } finally {
      setClaimsReady(true);
    }
  }, []);

  // Firebase auth listener (only when configured).
  useEffect(() => {
    if (!firebaseConfigured) {
      setStatus(uid ? 'loading' : 'signedOut');
      return;
    }
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      setUid(fbUser?.uid ?? null);
      setEmail(fbUser?.email ?? null);
      setDisplayName(fbUser?.displayName ?? null);
      setEmailVerified(fbUser?.emailVerified ?? false);
      // Persist uid so a page refresh restores the session synchronously
      // instead of flashing "signedOut" -> /login before Firebase rehydrates.
      try {
        if (fbUser?.uid) {
          localStorage.setItem(STORAGE_KEY, fbUser.uid);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        /* noop */
      }
      void loadClaims();
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseConfigured]);

  // The real signed-in user — independent of impersonation. Status, claims and
  // the admin gate always resolve against this identity.
  const realUserQuery = useLiveQuery<UserDoc>(
    uid ? COL.users : null,
    uid ? [where('uid', '==', uid)] : [],
    Boolean(uid),
  );
  // Fallback: every in-repo writer uses the Firebase UID as the document id,
  // so a doc-id lookup still finds the user when the stored `uid` field is
  // missing or mismatched (e.g. data inserted manually/another script).
  const idDocQuery = useLiveDoc<UserDoc>(uid ? COL.users : null, uid);
  const realUser = realUserQuery.data[0] ?? idDocQuery.data ?? null;

  const impersonatedQuery = useLiveQuery<UserDoc>(
    impersonatedUserId ? COL.users : null,
    impersonatedUserId ? [where('uid', '==', impersonatedUserId)] : [],
    Boolean(impersonatedUserId),
  );
  const impersonatedUser = impersonatedQuery.data[0] ?? null;
  const isImpersonating = Boolean(impersonatedUserId && impersonatedUser);
  const effectiveUser = impersonatedUser ?? realUser;

  useEffect(() => {
    if (!uid) {
      setStatus('signedOut');
    } else if (!emailVerified && email) {
      // Email/password accounts must confirm their address before they can
      // onboard. Google sign-in users are verified by default, so this only
      // gates email-registered accounts.
      setStatus('unverified');
    } else if (realUser) {
      setStatus('signedIn');
    } else if (realUserQuery.error || idDocQuery.error) {
      // A transient error (e.g. the first anonymous fetch gets a 403 before
      // the Firebase ID token is ready after a refresh) must not be treated as
      // "no profile found". Keep loading; the client refetches once identity
      // arrives and the effect below resolves the status.
      setStatus('loading');
    } else if (!realUserQuery.loading && !idDocQuery.loading) {
      setStatus('onboarding');
    }
  }, [
    uid,
    email,
    emailVerified,
    realUser,
    realUserQuery.loading,
    realUserQuery.error,
    idDocQuery.loading,
    idDocQuery.error,
  ]);
  // Role-scoped live subscriptions — keyed to the effective user so they
  // re-scope automatically when an admin impersonates another role.
  const mahasiswaQuery = useLiveQuery<MahasiswaDoc>(
    effectiveUser?.role === 'mahasiswa' ? COL.mahasiswa : null,
    effectiveUser ? [where('userId', '==', effectiveUser.id)] : [],
    Boolean(effectiveUser && effectiveUser.role === 'mahasiswa'),
  );
  const dosenRolesQuery = useLiveQuery<DosenRoleDoc>(
    effectiveUser?.role === 'dosen' ? COL.dosenRoles : null,
    effectiveUser
      ? [where('dosenId', '==', effectiveUser.id), where('status', '==', 'active')]
      : [],
    Boolean(effectiveUser && effectiveUser.role === 'dosen'),
  );
  const teamRolesQuery = useLiveQuery<SkripsiTeamDoc>(
    effectiveUser?.role === 'dosen' ? COL.skripsiTeam : null,
    effectiveUser ? [where('dosenId', '==', effectiveUser.id)] : [],
    Boolean(effectiveUser && effectiveUser.role === 'dosen'),
  );
  // Prodi the user leads as kaprodi (a dosen per-prodi capability).
  const kaprodiProdiQuery = useLiveQuery<ProdiDoc>(
    effectiveUser?.role === 'dosen' ? COL.prodi : null,
    effectiveUser
      ? [where('kaprodiId', '==', effectiveUser.id), where('status', '==', 'active')]
      : [],
    Boolean(effectiveUser && effectiveUser.role === 'dosen'),
  );
  // All active prodi — used to resolve a staf_jurusan's scope: they serve every
  // prodi under the jurusan they belong to (no prodi-level staff).
  const stafJurusanProdiQuery = useLiveQuery<ProdiDoc>(
    effectiveUser?.role === 'staf_jurusan' ? COL.prodi : null,
    effectiveUser ? [where('status', '==', 'active')] : [],
    Boolean(effectiveUser && effectiveUser.role === 'staf_jurusan'),
  );

  const ctx = useMemo<CurrentUser | null>(() => {
    if (!effectiveUser) return null;
    const isAdmin = effectiveUser.role === 'admin' && claims.admin === true;
    const jurusanIds = effectiveUser.jurusanIds ?? [];
    // For staf_jurusan, prodi scope = every active prodi under their jurusan.
    const prodiIds =
      effectiveUser.role === 'staf_jurusan'
        ? stafJurusanProdiQuery.data
            .filter((p) => jurusanIds.includes(p.jurusanId))
            .map((p) => p.id)
        : (effectiveUser.prodiIds ?? []);
    return {
      user: effectiveUser,
      isAdmin,
      mahasiswa: effectiveUser.role === 'mahasiswa' ? (mahasiswaQuery.data[0] ?? null) : null,
      dosenRoles: effectiveUser.role === 'dosen' ? dosenRolesQuery.data.map((r) => r.role) : [],
      teamRoles: effectiveUser.role === 'dosen' ? teamRolesQuery.data : [],
      prodiIds,
      jurusanIds,
      kaprodiProdiIds: kaprodiProdiQuery.data.map((p) => p.id),
    };
  }, [
    effectiveUser,
    claims,
    mahasiswaQuery.data,
    dosenRolesQuery.data,
    teamRolesQuery.data,
    kaprodiProdiQuery.data,
    stafJurusanProdiQuery.data,
  ]);

  const signInGoogle = useCallback(async () => {
    await signInWithPopup(auth, googleProvider);
  }, []);

  const signInEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const registerEmail = useCallback(async (email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    // Force a fresh token refresh so claims/uid sync before the status effect
    // resolves; the ID token is minted right after account creation.
    try {
      await cred.user.getIdToken(true);
    } catch {
      /* noop */
    }
  }, []);

  const sendVerificationEmail = useCallback(async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    await sendEmailVerification(fbUser);
  }, []);

  const reloadUser = useCallback(async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    await fbUser.reload();
    setEmailVerified(fbUser.emailVerified ?? false);
    if (fbUser.emailVerified) void loadClaims();
  }, [loadClaims]);

  const startImpersonation = useCallback(
    (userId: string) => {
      const isRealAdmin = realUser?.role === 'admin' && claims.admin === true;
      if (!isRealAdmin) return;
      setImpersonatedUserId(userId);
    },
    [realUser, claims],
  );

  const stopImpersonation = useCallback(() => {
    setImpersonatedUserId(null);
  }, []);

  const signOut = useCallback(async () => {
    if (firebaseConfigured) {
      try {
        await fbSignOut(auth);
      } catch {
        /* noop */
      }
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
    setUid(null);
    setImpersonatedUserId(null);
  }, []);

  const refresh = useCallback(() => {
    void loadClaims();
  }, [loadClaims]);

  // Real-admin gate (unaffected by impersonation) so switcher entry points and
  // the AdminNotRecognized guard never see the impersonated identity.
  const isAdmin = Boolean(realUser?.role === 'admin' && claims.admin === true);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      uid,
      user: effectiveUser,
      ctx,
      claims,
      isAdmin,
      claimsReady,
      email,
      displayName,
      emailVerified,
      realUser,
      isImpersonating,
      impersonatedUser,
      startImpersonation,
      stopImpersonation,
      signInGoogle,
      signInEmail,
      registerEmail,
      sendVerificationEmail,
      reloadUser,
      signOut,
      refresh,
    }),
    [
      status,
      uid,
      effectiveUser,
      ctx,
      claims,
      isAdmin,
      claimsReady,
      email,
      displayName,
      emailVerified,
      realUser,
      isImpersonating,
      impersonatedUser,
      startImpersonation,
      stopImpersonation,
      signInGoogle,
      signInEmail,
      registerEmail,
      sendVerificationEmail,
      reloadUser,
      signOut,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}

/**
 * The signed-in user's context; throws when absent. Use in role-scoped
 * screens (behind `RoleRoute`, which only renders once `ctx.user.role` is
 * set) instead of `ctx!` — the guard is the justification, stated once here.
 */
export function useCtx(): CurrentUser {
  const { ctx } = useAuth();
  if (!ctx) throw new Error('useCtx requires a signed-in user (RoleRoute guarantees ctx)');
  return ctx;
}
